import { FC, useMemo, useState, useEffect, useRef } from 'react'
import { canEditCrewDataAtOfficeReview } from '../core/crewOfficeEditAccess'
import { toast, ToastContainer } from 'react-toastify'
import { KTSVG } from '../../../../_metronic/helpers'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { useAuth } from '../../auth'
import {
  CREWING_ACTION_PERMISSIONS,
  CREWING_READ_PERMISSIONS,
  useCrewingCanRead,
  useCrewingPermissionRevision,
} from '../core/crewingPermissionAccess'
import { useCrewingRoleAccess } from '../core/crewingRoleAccess'
import { canViewCrewFinancialDrafts } from '../core/crewFinancialDraftAccess'
import {hasFinancialDraftDelegation, canAccessFinancialDraft} from '../core/financialManagementDraftAccess'
import { Rank, Crew, Company, CompanyAdmin, Vessel } from '../core/_models'
import {
  getCrewDeductions,
  createCrewDeduction,
  updateCrewDeduction,
  submitCrewDeduction,
  reviewCrewDeduction,
  rejectCrewDeduction,
  uploadCrewDeductionAttachment,
  deleteCrewDeductionAttachment,
  CrewDeduction,
  DeductionStatus,
  CreateCrewDeductionRequest,
  UpdateCrewDeductionRequest,
  getRanksforList,
  getCrewList,
  getCompanyList,
  getCompanyAdminList,
  getCrewLatestBankingDetails,
  crewDeductionAttachmentViewUrl,
  crewDeductionAttachmentDownloadUrl,
  getSignOnOffRecords,
  SignOnOffRecord,
} from '../core/_requests'
import CrewDeductionModal from '../components/CrewDeductionModal'
import ViewCrewDeductionModal from '../components/ViewCrewDeductionModal'
import CrewDeductionReviewModal from '../components/CrewDeductionReviewModal'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../shared/access/accessibleVessels'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import CrewDeductionAttachmentsModal from '../components/CrewDeductionAttachmentsModal'
import CrewDeductionExportModal from '../components/CrewDeductionExportModal'
import { FileViewerModal } from '../../QHSE/components/FileViewerModal'
import { RemarksCell, RemarksModal } from '../../QHSE/components/StructuredRemarksView'
import {
  buildCrewSignOnDateMap,
  getCrewSignOnMonth,
  toSignOnMonthKey,
} from '../core/signOnDateUtils'
import {
  applyCrewingPresentationFilters,
  allowCompanyPresentationFilters,
  extractRows,
} from '../core/crewingPresentationFilters'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'
import {
  blurNumberInputOnWheel,
  preventNumberInputArrowStep,
} from '../../../shared/utils/numberInputGuards'

type SortConfig = {
  key: keyof CrewDeduction | 'total'
  direction: 'asc' | 'desc'
}

type DeductionInlineDraft = {
  claimMonth: string
  deductionCashAdvanceFromOffice: string
  deductionBond: string
  deductionInternetCard: string
  deductionOtherDeductions: string
  firstTimeDeduction: string
  remarks: string
}

const getCurrentPeriod = () => {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  return `${now.getFullYear()}-${month}`
}

const toMonthValue = (monthKey: string) => (monthKey ? `${monthKey}-01` : '')

const getMonthKey = (value?: string | null): string => {
  if (!value) return ''
  const source = String(value).trim()
  const matched = source.match(/^(\d{4})-(\d{2})/)
  if (matched) {
    return `${matched[1]}-${matched[2]}`
  }

  const parsed = new Date(source)
  if (Number.isNaN(parsed.getTime())) return ''
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  return `${parsed.getFullYear()}-${month}`
}

const formatMonthKeyLabel = (monthKey: string): string => {
  if (!monthKey) return '-'
  const [yearStr, monthStr] = monthKey.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return monthKey
  }
  return new Date(year, month - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
}

const CrewDeductions: FC = () => {
  const { currentUser, hasPermission, isSuperAdmin } = useAuth()
  const {
    isCrew,
    effectiveBehavior,
    isOfficeRole,
    isCompanyGroupAdmin,
    isCompanyAdmin: baseIsCompanyAdmin,
    isOperator,
  } = useCrewingRoleAccess()
  const roleId = currentUser?.role?.id
  const canManageOfficeDraft = hasFinancialDraftDelegation(effectiveBehavior, hasPermission('MANAGE_CREW_DEDUCTION_DRAFT'))
  const canViewDrafts = canViewCrewFinancialDrafts({isSuperAdmin, isOfficeRole}) || canManageOfficeDraft

  // Role-based permissions
  const operatorCompanyAdminId =
    Number((currentUser as any)?.companyAdminId ?? (currentUser as any)?.companyAdmin?.id ?? 0) ||
    undefined
  const isCompanyAdmin = baseIsCompanyAdmin || (isOperator && !!operatorCompanyAdminId) // SubCompany
  const crewRankName = ((currentUser as any)?.rank?.rank ?? '').toLowerCase()
  const crewRankId = Number((currentUser as any)?.rank?.id ?? 0)
  const isCrewMaster = isCrew && (crewRankName.includes('master') || crewRankId === 1)
  const currentCrewId = isCrew ? Number((currentUser as any)?.roleEntityId ?? 0) : 0
  const currentCrewUsername = currentUser?.username ?? ''
  const currentCrewName = (currentUser as any)?.roleEntityName ?? currentCrewUsername
  const roleEntityId = Number((currentUser as any)?.roleEntityId ?? 0)

  const fixedCompanyGroupId = useMemo(() => {
    if (isCompanyGroupAdmin) {
      return (
        Number(
          (currentUser as any)?.companyGroupAdminId ??
          (currentUser as any)?.companyGroupAdmin?.id ??
          (roleEntityId || 0)
        ) || undefined
      )
    }
    if (isCompanyAdmin) {
      return (
        Number(
          (currentUser as any)?.companyGroupAdminId ??
          (currentUser as any)?.companyAdmin?.cga?.id ??
          0
        ) || undefined
      )
    }
    return undefined
  }, [currentUser, isCompanyGroupAdmin, isCompanyAdmin, roleEntityId])

  const fixedCompanyAdminId = useMemo(() => {
    if (!isCompanyAdmin) return undefined
    return (
      Number(
        (currentUser as any)?.companyAdminId ??
        (currentUser as any)?.companyAdmin?.id ??
        (roleEntityId || 0)
      ) || undefined
    )
  }, [currentUser, isCompanyAdmin, roleEntityId])

  const allowCompanyFilters = allowCompanyPresentationFilters(isSuperAdmin, roleId, isOperator)

  const canReadDeductions = useCrewingCanRead(CREWING_READ_PERMISSIONS.DEDUCTION)
  const permissionRevision = useCrewingPermissionRevision()

  const showCompanyFilter = isSuperAdmin
  const showSubCompanyFilter = isSuperAdmin || isCompanyGroupAdmin
  const showVesselFilter = isSuperAdmin || isCompanyGroupAdmin || isCompanyAdmin || isOperator

  // Super Admin and vessel masters can create/edit/submit deductions.
  const canCreate = isSuperAdmin || hasPermission('CREATE_CREW_DEDUCTION')
  const canApprove = isSuperAdmin || (hasPermission('APPROVE_CREW_DEDUCTION') && isOfficeRole)
  const canSubmit = isSuperAdmin || (canManageOfficeDraft || (!isOfficeRole && hasPermission('APPROVE_CREW_DEDUCTION')))
  const canExportDeduction =
    isSuperAdmin ||
    hasPermission('EXPORT_CREW_DEDUCTION') ||
    hasPermission('EXPORT_DEDUCTION') ||
    hasPermission('DOWNLOAD_CREW_DEDUCTION')
  const canDownloadDeduction =
    isSuperAdmin ||
    hasPermission('DOWNLOAD_CREW_DEDUCTION') ||
    hasPermission('EXPORT_CREW_DEDUCTION') ||
    hasPermission('EXPORT_DEDUCTION')

  const useCrewDropdownFilter = isCompanyGroupAdmin || isCompanyAdmin

  const [claims, setClaims] = useState<CrewDeduction[]>([])
  const [ranks, setRanks] = useState<Rank[]>([])
  const [crewMembers, setCrewMembers] = useState<Crew[]>([])
  const [modalCrewMembers, setModalCrewMembers] = useState<Crew[]>([])
  const [companies, setCompanies] = useState<Company[]>([])
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [selectedCompanyGroupId, setSelectedCompanyGroupId] = useState<number | ''>('')
  const [selectedCompanyAdminId, setSelectedCompanyAdminId] = useState<number | ''>('')
  const [selectedVesselId, setSelectedVesselId] = useState<number | ''>('')

  const { state: globalState } = useGlobalFilter()

  useEffect(() => {
    setSearchTerm(globalState.searchQuery || '')
  }, [globalState.searchQuery])

  useEffect(() => {
    if (globalState.vessel?.id) {
      setSelectedVesselId(Number(globalState.vessel.id))
    } else {
      setSelectedVesselId('')
    }

    if (globalState.company) {
      if (globalState.company.type === 'company_group') {
        setSelectedCompanyGroupId(Number(globalState.company.id))
        setSelectedCompanyAdminId('')
      } else {
        setSelectedCompanyAdminId(Number(globalState.company.id))
        setSelectedCompanyGroupId('')
      }
    } else {
      setSelectedCompanyGroupId('')
      setSelectedCompanyAdminId('')
    }
  }, [globalState.vessel, globalState.company])
  const [modalCompanyGroupId, setModalCompanyGroupId] = useState<number | ''>('')
  const [modalCompanyAdminId, setModalCompanyAdminId] = useState<number | ''>('')
  const [modalVesselId, setModalVesselId] = useState<number | ''>('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [currencyByCrewId, setCurrencyByCrewId] = useState<Record<number, string>>({})
  const [inlineDrafts, setInlineDrafts] = useState<Record<string, DeductionInlineDraft>>({})
  const [inlineSavingClaimIds, setInlineSavingClaimIds] = useState<Record<string, boolean>>({})

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCrewId, setSelectedCrewId] = useState<number | ''>('')
  const [statusFilter, setStatusFilter] = useState<'All' | DeductionStatus>('All')
  const [selectedMonth, setSelectedMonth] = useState(getCurrentPeriod())
  const [crewRosterPeriod, setCrewRosterPeriod] = useState('')
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'rankName', direction: 'asc' })
  const [editingClaim, setEditingClaim] = useState<CrewDeduction | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isNewClaim, setIsNewClaim] = useState(false)

  // View modal state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [viewClaim, setViewClaim] = useState<CrewDeduction | null>(null)

  // Attachments modal state
  const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false)
  const [attachmentsModalClaim, setAttachmentsModalClaim] = useState<CrewDeduction | null>(null)

  // Review modal state
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)
  const [reviewingClaim, setReviewingClaim] = useState<CrewDeduction | null>(null)
  const [isReviewSaving, setIsReviewSaving] = useState(false)

  // Export modal state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)

  // File viewer modal state
  const [fileModal, setFileModal] = useState<{
    visible: boolean
    title: string
    fileName?: string
    viewUrl?: string
    downloadUrl?: string
  }>({
    visible: false,
    title: '',
  })
  const [remarksModal, setRemarksModal] = useState<{
    visible: boolean
    title: string
    subtitle: string
    text: string
  }>({
    visible: false,
    title: '',
    subtitle: '',
    text: '',
  })
  const [editingRemarksRecord, setEditingRemarksRecord] = useState<{
    claim: CrewDeduction
    text: string
  } | null>(null)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const didInitFiltersRef = useRef(false)
  const modalCrewFetchSeqRef = useRef(0)
  const crewFetchSeqRef = useRef(0)
  const [isCrewLoading, setIsCrewLoading] = useState(true)
  const [crewStatusFilter, setCrewStatusFilter] = useState<'ALL' | 'SIGNED_ON' | 'SIGNED_OFF'>('ALL')
  const [crewAssignmentStatus, setCrewAssignmentStatus] = useState<Record<number, string>>({})

  // Fetch claims, ranks, and crew members on mount
  useEffect(() => {
    if (!roleId || !canReadDeductions) return
    fetchClaims()
    fetchRanks()
    if (showSubCompanyFilter) {
      fetchCompanies()
    }
    if (showCompanyFilter) {
      fetchCompanyAdmins()
    }
    if (showVesselFilter) {
      fetchVessels()
    }
    fetchSignOnOff()
  }, [roleId, canReadDeductions, canCreate, showCompanyFilter, showSubCompanyFilter, showVesselFilter, permissionRevision])

  useEffect(() => {
    if (didInitFiltersRef.current) return

    if (isCompanyGroupAdmin) {
      const cgaId = currentUser?.companyGroupAdminId ?? currentUser?.companyGroupAdmin?.id
      if (cgaId) {
        setSelectedCompanyGroupId(cgaId)
      }
    }

    if (isCompanyAdmin) {
      const caId = currentUser?.companyAdminId ?? currentUser?.companyAdmin?.id
      if (caId) {
        setSelectedCompanyAdminId(caId)
      }
    }

    if (isCrew) {
      const vesselId = currentUser?.vessel?.id
      if (vesselId) {
        setSelectedVesselId(vesselId)
      }
    }

    didInitFiltersRef.current = true
  }, [currentUser, isCompanyGroupAdmin, isCompanyAdmin, isCrew])

const fetchClaims = async () => {
  setIsLoading(true)
  try {
    const data = extractRows<CrewDeduction>(await getCrewDeductions())
    const scopedData =
      isCrew && !isCrewMaster && currentCrewId > 0
        ? data.filter((claim) => claim.crewId === currentCrewId)
        : data
    setClaims(canReadDeductions ? scopedData : [])

    const uniqueCrewIds = Array.from(
      new Set(
        scopedData
          .map((claim) => Number(claim.crewId))
          .filter((crewId) => Number.isFinite(crewId) && crewId > 0)
      )
    )

    if (uniqueCrewIds.length === 0) {
      setCurrencyByCrewId({})
    } else {
      const currencyRows = await Promise.all(
        uniqueCrewIds.map(async (crewId) => {
          const banking = await getCrewLatestBankingDetails(crewId)
          return {
            crewId,
            salaryCurrency: banking?.salaryCurrency,
            salaryOtherCurrency: banking?.salaryOtherCurrency,
          }
        })
      )

      const nextCurrencyByCrewId: Record<number, string> = {}
      currencyRows.forEach((row) => {
        const salaryCurrency = (row.salaryCurrency || '').toString().trim().toUpperCase()
        const salaryOtherCurrency = (row.salaryOtherCurrency || '')
          .toString()
          .trim()
          .toUpperCase()
        const resolvedCurrency = salaryCurrency === 'OTHER' ? salaryOtherCurrency : salaryCurrency

        if (resolvedCurrency) {
          nextCurrencyByCrewId[row.crewId] = resolvedCurrency
        }
      })
      setCurrencyByCrewId(nextCurrencyByCrewId)
    }
  } catch (error) {
    console.error('Error fetching deductions:', error)
    showErrorToast(error, 'Failed to fetch deductions', 'load')
  } finally {
    setIsLoading(false)
  }
}

const fetchRanks = async () => {
  try {
    const data = await getRanksforList()
    setRanks(data)
  } catch (error) {
    console.error('Error fetching ranks:', error)
    showErrorToast(error, 'Failed to fetch ranks', 'load')
  }
}

const fetchCrewMembers = async (filters?: {
  companyGroupId?: number
  companyAdminId?: number
  vesselId?: number
}) => {
  setIsCrewLoading(true)
  const requestSeq = ++crewFetchSeqRef.current
  try {
    const data = await getCrewList({...filters, period: selectedMonth || getCurrentPeriod()})
    if (requestSeq === crewFetchSeqRef.current) {
      setCrewMembers(data)
      setCrewRosterPeriod(selectedMonth || getCurrentPeriod())
    }
  } catch (error) {
    if (requestSeq === crewFetchSeqRef.current) {
      console.error('Error fetching crew members:', error)
      showErrorToast(error, 'Failed to fetch crew members', 'load')
    }
  } finally {
    if (requestSeq === crewFetchSeqRef.current) {
      setIsCrewLoading(false)
    }
  }
}

const fetchModalCrewMembers = async (
  filters?: {
    companyGroupId?: number
    companyAdminId?: number
    vesselId?: number
  },
  requestSeq?: number
) => {
  try {
    const data = await getCrewList({...filters, period: selectedMonth || getCurrentPeriod()})
    if (requestSeq && requestSeq !== modalCrewFetchSeqRef.current) {
      return
    }
    setModalCrewMembers(data)
  } catch (error) {
    if (requestSeq && requestSeq !== modalCrewFetchSeqRef.current) {
      return
    }
    console.error('Error fetching crew members for modal:', error)
    showErrorToast(error, 'Failed to fetch crew members', 'load')
  }
}

const fetchCompanies = async () => {
  try {
    const data = await getCompanyList()
    setCompanies(data)
  } catch (error) {
    console.error('Error fetching companies:', error)
    showErrorToast(error, 'Failed to fetch companies', 'load')
  }
}

const fetchCompanyAdmins = async () => {
  try {
    const data = await getCompanyAdminList()
    setCompanyAdmins(data)
  } catch (error) {
    console.error('Error fetching sub-companies:', error)
    showErrorToast(error, 'Failed to fetch sub-companies', 'load')
  }
}

const fetchVessels = async () => {
  try {
    const data = normalizeAccessibleVessels(await fetchAccessibleVesselsForCurrentUser())
    setVessels(data)
  } catch (error) {
    console.error('Error fetching vessels:', error)
    showErrorToast(error, 'Failed to fetch vessels', 'load')
  }
}

const fetchSignOnOff = async () => {
  try {
    const records = extractRows<SignOnOffRecord>(await getSignOnOffRecords())
    // Build crewId -> latest status map (by record id)
    const latestRecordByCrew = new Map<number, SignOnOffRecord>()
    records.forEach((record) => {
      const crewId = Number(record.crewId)
      if (crewId > 0) {
        const existing = latestRecordByCrew.get(crewId)
        if (!existing || record.id > existing.id) {
          latestRecordByCrew.set(crewId, record)
        }
      }
    })
    const statusMap: Record<number, string> = {}
    latestRecordByCrew.forEach((record, crewId) => {
      statusMap[crewId] = record.status
    })
    setCrewAssignmentStatus(statusMap)
  } catch (error) {
    console.error('Error fetching sign-on/off records:', error)
    showErrorToast(error, 'Failed to fetch sign on/off records', 'load')
  }
}

const calculateRowTotal = (claim: CrewDeduction) =>
  (claim.deductionCashAdvanceFromOffice || 0) +
  (claim.deductionBond || 0) +
  (claim.deductionOtherDeductions || 0) +
  (claim.deductionInternetCard || 0) +
  (claim.firstTimeDeduction || 0)

const hasUsedFirstTimeDeduction = (crewId: number) =>
  claims.some(
    (existingClaim) =>
      existingClaim.crewId === crewId && Number(existingClaim.firstTimeDeduction || 0) > 0
  )

const parseAmount = (value: string | number | null | undefined): number => {
  if (value == null || value === '') return 0
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const toInlineDraft = (claim: CrewDeduction): DeductionInlineDraft => ({
  claimMonth: getMonthKey(claim.claimMonth),
  deductionCashAdvanceFromOffice: claim.deductionCashAdvanceFromOffice ? String(claim.deductionCashAdvanceFromOffice) : '',
  deductionBond: (claim as any).deductionBond ? String((claim as any).deductionBond) : '',
  deductionInternetCard: claim.deductionInternetCard ? String(claim.deductionInternetCard) : '',
  deductionOtherDeductions: claim.deductionOtherDeductions ? String(claim.deductionOtherDeductions) : '',
  firstTimeDeduction: claim.firstTimeDeduction ? String(claim.firstTimeDeduction) : '',
  remarks: claim.remarks || '',
})

const getDraftKey = (claim: CrewDeduction) => {
  const monthKey = getMonthKey(claim.claimMonth)
  return `${claim.crewId}-${monthKey || 'na'}`
}

const getInlineDraft = (claim: CrewDeduction): DeductionInlineDraft =>
  inlineDrafts[getDraftKey(claim)] ?? toInlineDraft(claim)

const calculateDraftTotal = (draft: DeductionInlineDraft) =>
  parseAmount(draft.deductionCashAdvanceFromOffice) +
  parseAmount(draft.deductionBond) +
  parseAmount(draft.deductionInternetCard) +
  parseAmount(draft.deductionOtherDeductions) +
  parseAmount(draft.firstTimeDeduction)

const isRowDirty = (claim: CrewDeduction): boolean => {
  const draft = getInlineDraft(claim)
  const initial = toInlineDraft(claim)
  return (
    draft.claimMonth !== initial.claimMonth ||
    parseAmount(draft.deductionCashAdvanceFromOffice) !==
    parseAmount(initial.deductionCashAdvanceFromOffice) ||
    parseAmount(draft.deductionBond) !== parseAmount(initial.deductionBond) ||
    parseAmount(draft.deductionInternetCard) !== parseAmount(initial.deductionInternetCard) ||
    parseAmount(draft.deductionOtherDeductions) !==
    parseAmount(initial.deductionOtherDeductions) ||
    parseAmount(draft.firstTimeDeduction) !== parseAmount(initial.firstTimeDeduction) ||
    draft.remarks.trim() !== initial.remarks.trim()
  )
}

const updateInlineDraft = (
  claim: CrewDeduction,
  key: keyof DeductionInlineDraft,
  value: string
) => {
  const draftKey = getDraftKey(claim)
  setInlineDrafts((prev) => {
    const baseDraft = prev[draftKey] || {
      claimMonth: '',
      deductionCashAdvanceFromOffice: '',
      deductionBond: '',
      deductionInternetCard: '',
      deductionOtherDeductions: '',
      firstTimeDeduction: '',
      remarks: '',
    }

    const nextDraft: DeductionInlineDraft = {
      ...baseDraft,
      [key]: value,
    }

    return {
      ...prev,
      [draftKey]: nextDraft,
    }
  })
}

const resolveCurrencyCode = (
  salaryCurrency?: string | null,
  salaryOtherCurrency?: string | null
) => {
  const normalizedAccountCurrency = (salaryCurrency || '').toString().trim().toUpperCase()
  const normalizedOtherCurrency = (salaryOtherCurrency || '').toString().trim().toUpperCase()
  if (normalizedAccountCurrency === 'OTHER') return normalizedOtherCurrency || ''
  return normalizedAccountCurrency
}

const getClaimCurrency = (claim: CrewDeduction) => {
  const mappedCurrency = currencyByCrewId[claim.crewId]
  if (mappedCurrency) return mappedCurrency
  return resolveCurrencyCode(claim.salaryCurrency, claim.salaryOtherCurrency)
}

const getCurrencySymbol = (currencyCode?: string) => {
  const code = (currencyCode || '').toString().trim().toUpperCase()
  switch (code) {
    case 'USD':
      return '$'
    case 'EUR':
      return '\u20AC'
    case 'GBP':
      return '\u00A3'
    case 'SGD':
      return 'S$'
    case 'INR':
      return '\u20B9'
    case 'AED':
      return '\u062F.\u0625 '
    default:
      try {
        const part = new Intl.NumberFormat('en', {
          style: 'currency',
          currency: code,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
          .formatToParts(0)
          .find((p) => p.type === 'currency')
        const maybeSymbol = (part?.value || '').trim()
        return maybeSymbol && maybeSymbol.toUpperCase() !== code ? maybeSymbol : ''
      } catch {
        return ''
      }
  }
}

const formatClaimAmount = (claim: CrewDeduction, value: number) => {
  const symbol = getCurrencySymbol(getClaimCurrency(claim))
  return `${symbol}${formatCurrency(value)}`
}

const formatCurrency = (value: number) =>
  value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const handleSort = (key: keyof CrewDeduction | 'total') => {
  setSortConfig((prev) => ({
    key,
    direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
  }))
}

const handleTableHorizontalWheelScroll = (event: React.WheelEvent<HTMLDivElement>) => {
  const container = event.currentTarget
  if (container.scrollWidth <= container.clientWidth) return

  // Keep vertical scrolling native; use Shift + wheel for horizontal intent.
  const isShiftHorizontalIntent =
    event.shiftKey && Math.abs(event.deltaY) > Math.abs(event.deltaX)
  if (!isShiftHorizontalIntent || event.deltaY === 0) return

  const isScrollingRight = event.deltaY > 0
  const canScrollContainerRight =
    container.scrollLeft + container.clientWidth < container.scrollWidth - 1
  const canScrollContainerLeft = container.scrollLeft > 0
  if (
    (isScrollingRight && !canScrollContainerRight) ||
    (!isScrollingRight && !canScrollContainerLeft)
  )
    return

  event.preventDefault()
  container.scrollLeft += event.deltaY
}

const vesselById = useMemo(
  () => new Map(vessels.map((v) => [v.id, v])),
  [vessels]
)

const crewFilterOptions = useMemo(() => {
  const visibleCrew = canReadDeductions ? crewMembers : []
  const uniqueById = new Map<number, string>()

  applyCrewingPresentationFilters(visibleCrew, {
    selectedVesselId,
    selectedCrewId,
    allowCompanyPresentationFilters: allowCompanyFilters,
    selectedCompanyGroupId: allowCompanyFilters ? selectedCompanyGroupId : '',
    selectedCompanyAdminId: allowCompanyFilters ? selectedCompanyAdminId : '',
  }).forEach((crew) => {
    const crewId = Number(crew.id)
    if (Number.isFinite(crewId) && crewId > 0) {
      uniqueById.set(crewId, String(crew.name || `Crew ${crewId}`))
    }
  })

  if (uniqueById.size === 0 && canReadDeductions) {
    applyCrewingPresentationFilters(claims, {
      selectedVesselId,
      allowCompanyPresentationFilters: allowCompanyFilters,
      selectedCompanyGroupId: allowCompanyFilters ? selectedCompanyGroupId : '',
      selectedCompanyAdminId: allowCompanyFilters ? selectedCompanyAdminId : '',
    }).forEach((claim) => {
      const crewId = Number(claim.crewId)
      if (!uniqueById.has(crewId)) {
        uniqueById.set(crewId, String(claim.crewName || `Crew ${crewId}`))
      }
    })
  }

  return Array.from(uniqueById.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name))
}, [
  crewMembers,
  claims,
  selectedCompanyGroupId,
  selectedCompanyAdminId,
  selectedVesselId,
  selectedCrewId,
  allowCompanyFilters,
  canReadDeductions,
])

useEffect(() => {
  if (!selectedCrewId) return
  const exists = crewFilterOptions.some((crew) => crew.id === Number(selectedCrewId))
  if (!exists) setSelectedCrewId('')
}, [selectedCrewId, crewFilterOptions])

const crewSignOnDateByCrewId = useMemo(() => buildCrewSignOnDateMap(crewMembers), [crewMembers])

// The API validates historical months against all service periods.
const handleSelectedMonthChange = (value: string) => {
  setSelectedMonth(value)
}

const lockedMonthKey = selectedMonth || getCurrentPeriod()
const lockedClaimMonth = toMonthValue(lockedMonthKey)

useEffect(() => {
  setInlineDrafts(() => {
    const next: Record<string, DeductionInlineDraft> = {}
    claims.forEach((claim) => {
      next[getDraftKey(claim)] = toInlineDraft(claim)
    })
    return next
  })
}, [claims])

const filteredClaims = useMemo(() => {
  if (!canReadDeductions) return []

  const term = searchTerm.trim().toLowerCase()
  const monthRows = selectedMonth
    ? claims.filter((claim) => getMonthKey(claim.claimMonth) === selectedMonth)
    : claims

  const scopedMonthRows =
    isCrew && !isCrewMaster && currentCrewId > 0
      ? monthRows.filter((claim) => claim.crewId === currentCrewId)
      : monthRows

  const latestClaimByCrew = new Map<number, CrewDeduction>()
  scopedMonthRows.forEach((claim) => {
    const existing = latestClaimByCrew.get(claim.crewId)
    if (!existing || claim.id > existing.id) {
      latestClaimByCrew.set(claim.crewId, claim)
    }
  })

  const rankById = new Map<number, string>(ranks.map((r) => [r.id, r.rank]))
  const visibleCrewMembers =
    isCrew && !isCrewMaster
      ? crewMembers.filter((member) => {
        if (currentCrewId > 0 && member.id === currentCrewId) return true
        return !!currentCrewUsername && member.username === currentCrewUsername
      })
      : crewMembers

  const rowsFromRoster: CrewDeduction[] = Array.from(latestClaimByCrew.values())

  visibleCrewMembers.forEach((crew) => {
    if (crewRosterPeriod !== (selectedMonth || getCurrentPeriod())) return
    if (latestClaimByCrew.has(crew.id)) {
      return
    }

    const crewSignOnMonth = getCrewSignOnMonth(crew.id, crewSignOnDateByCrewId)
    const monthKey = selectedMonth || getCurrentPeriod()
    if (crewSignOnMonth && monthKey && monthKey < crewSignOnMonth) {
      return
    }

    const monthValue = selectedMonth ? `${selectedMonth}-01` : `${getCurrentPeriod()}-01`
    rowsFromRoster.push({
      id: 0,
      srNo: 0,
      crewId: crew.id,
      crewName: crew.name,
      vesselId: crew.vesselId ?? crew.vessel?.id ?? null,
      vesselName: crew.vesselName ?? crew.vessel?.fleet_name ?? null,
      rankId: crew.rankId ?? null,
      rankName:
        (crew as any)?.rankName ??
        (crew.rankId ? rankById.get(crew.rankId) || null : null) ??
        (crew as any)?.rank?.rank ?? null,
      claimMonth: monthValue,
      salary: 0,
      medical: 0,
      safetyGear: 0,
      travel: 0,
      boardingLodging: 0,
      hraAllowance: 0,
      bonus: 0,
      miscReimbursements: 0,
      totalAmount: 0,
      bfAmount: 0,
      wagesEarned: 0,
      allotmentAmount: 0,
      cashAdvance: 0,
      balanceCf: 0,
      unionSocialDues: 'As per CBA',
      deductionCashAdvanceFromOffice: 0,
      deductionBond: 0,
      deductionOtherDeductions: 0,
      deductionInternetCard: 0,
      firstTimeDeduction: 0,
      deductionsTotal: 0,
      remarks: '',
      officeRemarks: null,
      status: 'DRAFT',
      submittedAt: null,
      reviewedAt: null,
      reviewedByName: null,
      rejectionReason: null,
      salaryCurrency: null,
      salaryOtherCurrency: null,
      attachments: [],
      attachmentCount: 0,
      createdAt: '',
      updatedAt: '',
    })
  })

  return applyCrewingPresentationFilters(
    rowsFromRoster.filter((claim) => {
      const isDraftVisibleForRole = (claim.status !== 'DRAFT' && claim.id > 0) || canAccessFinancialDraft({
        isSuperAdmin, isOfficeRole, hasDelegation: canManageOfficeDraft, rowEligible: claim.canManageFinancialDraft})
      const matchesCrew = !selectedCrewId || claim.crewId === Number(selectedCrewId)
      const matchesSearch =
        (claim.crewName || '').toLowerCase().includes(term) ||
        (claim.rankName || '').toLowerCase().includes(term)
      const matchesStatus = statusFilter === 'All' || claim.status === statusFilter
      const matchesCrewStatus = crewStatusFilter === 'ALL' || crewAssignmentStatus[claim.crewId] === crewStatusFilter

      return (
        isDraftVisibleForRole &&
        (useCrewDropdownFilter ? matchesCrew : matchesSearch) &&
        matchesStatus &&
        matchesCrewStatus
      )
    }),
    {
      selectedVesselId,
      allowCompanyPresentationFilters: allowCompanyFilters,
      selectedCompanyGroupId: allowCompanyFilters ? selectedCompanyGroupId : '',
      selectedCompanyAdminId: allowCompanyFilters ? selectedCompanyAdminId : '',
    },
    { vesselById }
  )
}, [
  claims,
  crewRosterPeriod,
  crewMembers,
  ranks,
  searchTerm,
  selectedCrewId,
  statusFilter,
  selectedMonth,
  isCrew,
  isCrewMaster,
  currentCrewId,
  currentCrewUsername,
  canViewDrafts,
  isOfficeRole,
  isSuperAdmin,
  canManageOfficeDraft,
  useCrewDropdownFilter,
  crewSignOnDateByCrewId,
  canReadDeductions,
  selectedVesselId,
  selectedCompanyGroupId,
  selectedCompanyAdminId,
  allowCompanyFilters,
  vesselById,
  crewStatusFilter,
  crewAssignmentStatus,
])

// Reset to first page when filters change
useEffect(() => {
  setCurrentPage(1)
}, [
  searchTerm,
  selectedCrewId,
  statusFilter,
  selectedMonth,
  selectedCompanyGroupId,
  selectedCompanyAdminId,
  selectedVesselId,
  crewStatusFilter,
])

useEffect(() => {
  fetchCrewMembers({
    companyGroupId: allowCompanyFilters && selectedCompanyGroupId ? Number(selectedCompanyGroupId) : undefined,
    companyAdminId: allowCompanyFilters && selectedCompanyAdminId ? Number(selectedCompanyAdminId) : undefined,
    vesselId: selectedVesselId ? Number(selectedVesselId) : undefined,
  })
}, [
  selectedCompanyGroupId,
  selectedCompanyAdminId,
  selectedVesselId,
  allowCompanyFilters,
  selectedMonth,
])

useEffect(() => {
  if (!(isSuperAdmin || canManageOfficeDraft) || !canCreate || !isModalOpen || !isNewClaim) return

  modalCrewFetchSeqRef.current += 1
  const requestSeq = modalCrewFetchSeqRef.current
  setModalCrewMembers([])

  fetchModalCrewMembers(
    {
      companyGroupId: fixedCompanyGroupId ?? (modalCompanyGroupId ? Number(modalCompanyGroupId) : undefined),
      companyAdminId: fixedCompanyAdminId ?? (modalCompanyAdminId ? Number(modalCompanyAdminId) : undefined),
      vesselId: modalVesselId ? Number(modalVesselId) : undefined,
    },
    requestSeq
  )
}, [
  isSuperAdmin,
  canManageOfficeDraft,
  canCreate,
  isModalOpen,
  isNewClaim,
  modalCompanyGroupId,
  modalCompanyAdminId,
  modalVesselId,
  selectedMonth,
  fixedCompanyGroupId,
  fixedCompanyAdminId,
])

const effectiveCompanyGroupId =
  fixedCompanyGroupId ?? (selectedCompanyGroupId ? Number(selectedCompanyGroupId) : undefined)
const effectiveCompanyAdminId =
  fixedCompanyAdminId ?? (selectedCompanyAdminId ? Number(selectedCompanyAdminId) : undefined)

const filteredCompanyAdmins = useMemo(() => {
  if (!effectiveCompanyGroupId) return companies
  return companies.filter((ca) => {
    const parentCompanyId =
      (ca as any)?.cga?.id ??
      (ca as any)?.cgaid?.id ??
      (ca as any)?.companyGroupAdmin?.id ??
      (ca as any)?.companyGroupAdminId
    return parentCompanyId === effectiveCompanyGroupId
  })
}, [companies, effectiveCompanyGroupId])

const filteredModalCompanyAdmins = useMemo(() => {
  if (!modalCompanyGroupId) return companies
  return companies.filter((ca) => {
    const parentCompanyId =
      (ca as any)?.cga?.id ??
      (ca as any)?.cgaid?.id ??
      (ca as any)?.companyGroupAdmin?.id ??
      (ca as any)?.companyGroupAdminId
    return parentCompanyId === Number(modalCompanyGroupId)
  })
}, [companies, modalCompanyGroupId])

useEffect(() => {
  if (!modalCompanyAdminId) return
  const exists = filteredModalCompanyAdmins.some((ca) => ca.id === Number(modalCompanyAdminId))
  if (!exists) {
    setModalCompanyAdminId('')
    setModalVesselId('')
  }
}, [modalCompanyAdminId, filteredModalCompanyAdmins])

const shouldShowSubCompanyFilter =
  showSubCompanyFilter &&
  (isCompanyGroupAdmin
    ? filteredCompanyAdmins.length > 0
    : !!effectiveCompanyGroupId && filteredCompanyAdmins.length > 0)

useEffect(() => {
  if (!selectedCompanyAdminId) return
  const exists = filteredCompanyAdmins.some((ca) => ca.id === Number(selectedCompanyAdminId))
  if (!exists) {
    setSelectedCompanyAdminId('')
    setSelectedVesselId('')
  }
}, [selectedCompanyAdminId, filteredCompanyAdmins])

const filteredVessels = useMemo(() => {
  const getVesselCompanyAdminId = (v: Vessel): number | undefined => v.companyAdmin?.id ?? (v as any).companyAdminId ?? undefined
  const getVesselCompanyGroupId = (v: Vessel): number | undefined =>
    (v as any).companyGroupAdminId ?? v.companyGroupAdmin?.id ?? (v.companyAdmin as any)?.cgaid?.id ?? v.companyAdmin?.cga?.id

  if (effectiveCompanyAdminId) {
    return vessels.filter((v) => getVesselCompanyAdminId(v) === effectiveCompanyAdminId)
  }
  if (effectiveCompanyGroupId) {
    return vessels.filter((v) => getVesselCompanyGroupId(v) === effectiveCompanyGroupId)
  }
  return vessels
}, [vessels, effectiveCompanyAdminId, effectiveCompanyGroupId])

useEffect(() => {
  if (!selectedVesselId) return
  const exists = filteredVessels.some((v) => v.id === Number(selectedVesselId))
  if (!exists) setSelectedVesselId('')
}, [selectedVesselId, filteredVessels])

const selfCrewMember = useMemo(() => {
  if (!isCrew) return undefined
  if (currentCrewId) {
    const byId = crewMembers.find((member) => member.id === currentCrewId)
    if (byId) return byId
  }
  if (!currentCrewUsername) return undefined
  return crewMembers.find((member) => member.username === currentCrewUsername)
}, [isCrew, currentCrewId, currentCrewUsername, crewMembers])

const selectableCrewMembers = useMemo(() => {
  const scopedCrewMembers =
    !isCrew || isCrewMaster ? crewMembers : selfCrewMember ? [selfCrewMember] : []
  return scopedCrewMembers.filter((crew) => {
    const crewSignOnMonth = getCrewSignOnMonth(crew.id, crewSignOnDateByCrewId)
    return !(crewSignOnMonth && lockedMonthKey < crewSignOnMonth)
  })
}, [isCrew, isCrewMaster, crewMembers, selfCrewMember, crewSignOnDateByCrewId, lockedMonthKey])

const selectableModalCrewMembers = useMemo(() => {
  const sourceCrewMembers = (isSuperAdmin || canManageOfficeDraft) && isNewClaim ? modalCrewMembers : selectableCrewMembers
  return sourceCrewMembers.filter((crew) => {
    const crewSignOnMonth = getCrewSignOnMonth(crew.id, crewSignOnDateByCrewId)
    return !(crewSignOnMonth && lockedMonthKey < crewSignOnMonth)
  })
}, [
  isSuperAdmin,
  canManageOfficeDraft,
  isNewClaim,
  modalCrewMembers,
  selectableCrewMembers,
  crewSignOnDateByCrewId,
  lockedMonthKey,
])

const compareClaimsByRank = (a: CrewDeduction, b: CrewDeduction): number => {
  const aRankId = Number(a.rankId ?? 0)
  const bRankId = Number(b.rankId ?? 0)
  const hasRankIds = aRankId > 0 && bRankId > 0

  if (hasRankIds && aRankId !== bRankId) {
    return aRankId - bRankId
  }

  const rankNameCompare = String(a.rankName || '').localeCompare(String(b.rankName || ''))
  if (rankNameCompare !== 0) return rankNameCompare

  return String(a.crewName || '').localeCompare(String(b.crewName || ''))
}

const sortedClaims = useMemo(() => {
  const copy = [...filteredClaims]

  copy.sort((a, b) => {
    const rankCompare = compareClaimsByRank(a, b)
    if (rankCompare !== 0) {
      return rankCompare
    }

    const getValue = (claim: CrewDeduction) =>
      sortConfig.key === 'total'
        ? calculateRowTotal(claim)
        : claim[sortConfig.key as keyof CrewDeduction]
    const first = getValue(a)
    const second = getValue(b)
    if (typeof first === 'number' && typeof second === 'number') {
      return sortConfig.direction === 'asc' ? first - second : second - first
    }
    return sortConfig.direction === 'asc'
      ? String(first || '').localeCompare(String(second || ''))
      : String(second || '').localeCompare(String(first || ''))
  })
  return copy
}, [filteredClaims, sortConfig])

// Pagination calculations
const indexOfLastRecord = currentPage * rowsPerPage
const indexOfFirstRecord = indexOfLastRecord - rowsPerPage
const currentRecords = sortedClaims.slice(indexOfFirstRecord, indexOfLastRecord)
const totalPages = Math.max(1, Math.ceil(sortedClaims.length / rowsPerPage))

const handlePageChange = (page: number) => {
  if (page > 0 && page <= totalPages) setCurrentPage(page)
}

const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
  setRowsPerPage(parseInt(e.target.value, 10))
  setCurrentPage(1)
}

const totals = useMemo(() => {
  const totalAmount = filteredClaims.reduce((sum, claim) => sum + calculateRowTotal(claim), 0)
  const submitted = filteredClaims.filter((claim) => claim.status === 'SUBMITTED').length
  return {
    totalAmount,
    submitted,
    totalClaims: filteredClaims.length,
  }
}, [filteredClaims])

const handleOpenModal = (claim: CrewDeduction, isNew = false) => {
  setEditingClaim({
    ...claim,
    claimMonth: lockedClaimMonth,
  })
  setIsNewClaim(isNew)
  setIsModalOpen(true)
  if (isNew) {
    modalCrewFetchSeqRef.current += 1
    setModalCompanyGroupId('')
    setModalCompanyAdminId('')
    setModalVesselId('')
    setModalCrewMembers([])
  }
}

const handleCloseModal = () => {
  modalCrewFetchSeqRef.current += 1
  setIsModalOpen(false)
  setEditingClaim(null)
  setIsNewClaim(false)
  setModalCompanyGroupId('')
  setModalCompanyAdminId('')
  setModalVesselId('')
  setModalCrewMembers([])
}

const handleSaveClaim = async (pendingFiles?: File[]) => {
  if (!editingClaim) return

  if (isOfficeRole && !isSuperAdmin && (isNewClaim || editingClaim.status === 'DRAFT') && !editingClaim.vesselId) {
    toast.error('Please select a vessel')
    return
  }

  // Validate required fields for new claims
  if (isNewClaim) {
    if (!editingClaim.crewId || editingClaim.crewId === 0) {
      toast.error('Please select a crew member')
      return
    }
    if (!editingClaim.rankId) {
      toast.error('Please select a rank')
      return
    }
  }

  setIsSaving(true)

  try {
    if (isNewClaim) {
      const request: CreateCrewDeductionRequest = {
        crewId: editingClaim.crewId,
        vesselId: editingClaim.vesselId || undefined,
        rankId: editingClaim.rankId || undefined,
        claimMonth: lockedClaimMonth,
        unionSocialDues: editingClaim.unionSocialDues || 'As per CBA',
        deductionCashAdvanceFromOffice: editingClaim.deductionCashAdvanceFromOffice || 0,
        deductionBond: (editingClaim as any).deductionBond || 0,
        deductionOtherDeductions: editingClaim.deductionOtherDeductions || 0,
        deductionInternetCard: editingClaim.deductionInternetCard || 0,
        firstTimeDeduction: editingClaim.firstTimeDeduction || 0,
        deductionsTotal: calculateRowTotal(editingClaim),
        remarks: editingClaim.remarks,
      }
      const createdClaim = await createCrewDeduction(request)

      // Upload pending attachments if any
      if (pendingFiles && pendingFiles.length > 0 && createdClaim.id) {
        for (const file of pendingFiles) {
          try {
            await uploadCrewDeductionAttachment(createdClaim.id, file)
          } catch (uploadError) {
            console.error('Error uploading attachment:', uploadError)
            showErrorToast(uploadError, `Failed to upload ${file.name}`, 'save')
          }
        }
      }

      toast.success('Deduction created successfully')
    } else {
      const request: UpdateCrewDeductionRequest = {
        ...(isOfficeRole && !isSuperAdmin && editingClaim.status === 'DRAFT'
          ? {vesselId: editingClaim.vesselId || undefined} : {}),
        rankId: editingClaim.rankId || undefined,
        claimMonth: lockedClaimMonth,
        unionSocialDues: editingClaim.unionSocialDues || 'As per CBA',
        deductionCashAdvanceFromOffice: editingClaim.deductionCashAdvanceFromOffice || 0,
        deductionBond: (editingClaim as any).deductionBond || 0,
        deductionOtherDeductions: editingClaim.deductionOtherDeductions || 0,
        deductionInternetCard: editingClaim.deductionInternetCard || 0,
        firstTimeDeduction: editingClaim.firstTimeDeduction || 0,
        deductionsTotal: calculateRowTotal(editingClaim),
        remarks: editingClaim.remarks,
      }
      await updateCrewDeduction(editingClaim.id, request)
      toast.success('Deduction updated successfully')
    }
    await fetchClaims()
    handleCloseModal()
  } catch (error) {
    console.error('Error saving deduction:', error)
    showErrorToast(error, 'Failed to save deduction', isNewClaim ? 'create' : 'update')
  } finally {
    setIsSaving(false)
  }
}

const handleSubmitClaim = async (claim: CrewDeduction) => {
  if (!canSubmit || (isOfficeRole && !isSuperAdmin && claim.canManageFinancialDraft !== true)) {
    toast.error('You do not have permission to submit deductions')
    return
  }
  try {
    await submitCrewDeduction(claim.id)
    toast.success('Deduction submitted')
    await fetchClaims()
  } catch (error) {
    console.error('Error submitting deduction:', error)
    showErrorToast(error, 'Failed to submit deduction', 'save')
  }
}

const handleSaveInlineRow = async (claim: CrewDeduction): Promise<boolean> => {
  if (!canEditClaim(claim)) {
    toast.error('You do not have permission to edit this deduction')
    return false
  }
  const draft = getInlineDraft(claim)
  const payload: UpdateCrewDeductionRequest = {
    claimMonth: lockedClaimMonth,
    unionSocialDues: claim.unionSocialDues || 'As per CBA',
    deductionCashAdvanceFromOffice: parseAmount(draft.deductionCashAdvanceFromOffice),
    deductionBond: parseAmount(draft.deductionBond),
    deductionOtherDeductions: parseAmount(draft.deductionOtherDeductions),
    deductionInternetCard: parseAmount(draft.deductionInternetCard),
    firstTimeDeduction: parseAmount(draft.firstTimeDeduction),
    deductionsTotal: calculateDraftTotal(draft),
    remarks: draft.remarks,
    rankId: claim.rankId || undefined,
  }

  const savingKey = getDraftKey(claim)
  setInlineSavingClaimIds((prev) => ({ ...prev, [savingKey]: true }))
  try {
    if (claim.id > 0) {
      await updateCrewDeduction(claim.id, payload)
    } else {
      const createPayload: CreateCrewDeductionRequest = {
        crewId: claim.crewId,
        vesselId: claim.vesselId || undefined,
        rankId: claim.rankId || undefined,
        claimMonth: payload.claimMonth,
        unionSocialDues: payload.unionSocialDues,
        deductionCashAdvanceFromOffice: payload.deductionCashAdvanceFromOffice,
        deductionBond: payload.deductionBond,
        deductionOtherDeductions: payload.deductionOtherDeductions,
        deductionInternetCard: payload.deductionInternetCard,
        firstTimeDeduction: payload.firstTimeDeduction,
        deductionsTotal: payload.deductionsTotal,
        remarks: payload.remarks,
      }
      await createCrewDeduction(createPayload)
    }
    await fetchClaims()
    toast.success('Deduction saved')
    return true
  } catch (error) {
    console.error('Error saving deduction row:', error)
    showErrorToast(error, 'Failed to save deduction', claim.id > 0 ? 'update' : 'create')
    return false
  } finally {
    setInlineSavingClaimIds((prev) => ({ ...prev, [savingKey]: false }))
  }
}

const handleSubmitInlineRow = async (claim: CrewDeduction) => {
  if (!canSubmit || (isOfficeRole && !isSuperAdmin && claim.canManageFinancialDraft !== true)) {
    toast.error('You do not have permission to submit deductions')
    return
  }
  const targetMonthKey = lockedMonthKey

  const needsSave = isRowDirty(claim) || claim.id <= 0
  if (needsSave) {
    const didSave = await handleSaveInlineRow(claim)
    if (!didSave) return
  }

  if (claim.id > 0) {
    await handleSubmitClaim(claim)
    return
  }

  try {
    const all = await getCrewDeductions()
    const created = all
      .filter(
        (row) =>
          row.crewId === claim.crewId &&
          (!targetMonthKey || getMonthKey(row.claimMonth) === targetMonthKey)
      )
      .sort((a, b) => b.id - a.id)[0]

    if (!created?.id) {
      toast.error('Could not find created deduction to submit')
      return
    }

    await submitCrewDeduction(created.id)
    toast.success('Deduction submitted')
    await fetchClaims()
  } catch (error) {
    console.error('Error submitting created deduction:', error)
    showErrorToast(error, 'Failed to submit deduction', 'save')
  }
}

const handleNewClaim = async () => {
  const selectedOrCurrentMonth = selectedMonth || getCurrentPeriod()
  const defaultCrew = !isCrew || isCrewMaster ? null : selfCrewMember ?? null
  const defaultCrewSignOnMonth = toSignOnMonthKey(defaultCrew?.latestSignOnDate)
  const defaultMonthKey =
    defaultCrewSignOnMonth && selectedOrCurrentMonth < defaultCrewSignOnMonth
      ? defaultCrewSignOnMonth
      : selectedOrCurrentMonth
  const defaultMonth = `${defaultMonthKey}-01`
  const defaultCrewName = defaultCrew?.name ?? (isCrew && !isCrewMaster ? currentCrewName : '')
  const defaultRankId =
    defaultCrew?.rankId ?? (isCrew && !isCrewMaster ? crewRankId || null : null)
  const defaultRankName =
    (defaultRankId ? ranks.find((rank) => rank.id === defaultRankId)?.rank : undefined) ??
    (isCrew && !isCrewMaster ? (currentUser as any)?.rank?.rank : undefined) ??
    ''

  const defaultCrewId = defaultCrew?.id ?? (isCrew && !isCrewMaster ? currentCrewId : 0)
  let prefetchedSalary = 0
  let prefetchedAccountCurrency: string | null = null
  let prefetchedOtherCurrency: string | null = null
  let prefetchedFirstTimeDeduction = 0
  if (defaultCrewId && defaultCrewId > 0) {
    try {
      const latestBanking = await getCrewLatestBankingDetails(defaultCrewId)
      const numericSalary = Number(latestBanking?.salary ?? 0)
      prefetchedSalary = Number.isFinite(numericSalary) ? numericSalary : 0
      prefetchedAccountCurrency = (latestBanking?.salaryCurrency ?? null) as string | null
      prefetchedOtherCurrency = latestBanking?.salaryOtherCurrency ?? null
      const hasUsedOneTimeDeduction = hasUsedFirstTimeDeduction(defaultCrewId)
      const oneTimeDeductionFromBanking = Number(latestBanking?.deductionsTotal ?? 0)
      prefetchedFirstTimeDeduction =
        !hasUsedOneTimeDeduction && Number.isFinite(oneTimeDeductionFromBanking)
          ? oneTimeDeductionFromBanking
          : 0

      const resolvedCurrency = resolveCurrencyCode(
        prefetchedAccountCurrency,
        prefetchedOtherCurrency
      )
      if (resolvedCurrency) {
        setCurrencyByCrewId((prev) => ({
          ...prev,
          [defaultCrewId]: resolvedCurrency,
        }))
      }
    } catch (error) {
      console.error('Error prefetching crew salary for new claim:', error)
      prefetchedSalary = 0
    }
  }

  const newClaim: CrewDeduction = {
    id: 0,
    srNo: claims.length + 1,
    crewId: defaultCrewId,
    crewName: defaultCrewName,
    vesselId:
      defaultCrew?.vesselId ??
      defaultCrew?.vessel?.id ??
      (isCrew && !isCrewMaster ? currentUser?.vessel?.id ?? null : null),
    vesselName:
      defaultCrew?.vessel?.fleet_name ??
      defaultCrew?.vesselName ??
      (isCrew && !isCrewMaster ? currentUser?.vessel?.fleet_name ?? null : null),
    rankId: defaultRankId,
    rankName: defaultRankName,
    claimMonth: defaultMonth,
    salary: prefetchedSalary,
    medical: 0,
    safetyGear: 0,
    travel: 0,
    boardingLodging: 0,
    hraAllowance: 0,
    bonus: 0,
    miscReimbursements: 0,
    totalAmount: 0,
    bfAmount: 0,
    wagesEarned: 0,
    allotmentAmount: 0,
    cashAdvance: 0,
    balanceCf: 0,
    unionSocialDues: 'As per CBA',
    deductionCashAdvanceFromOffice: 0,
    deductionBond: 0,
    deductionOtherDeductions: 0,
    deductionInternetCard: 0,
    firstTimeDeduction: prefetchedFirstTimeDeduction,
    deductionsTotal: prefetchedFirstTimeDeduction,
    remarks: '',
    officeRemarks: null,
    status: 'DRAFT',
    submittedAt: null,
    reviewedAt: null,
    reviewedByName: null,
    rejectionReason: null,
    salaryCurrency: prefetchedAccountCurrency,
    salaryOtherCurrency: prefetchedOtherCurrency,
    attachments: [],
    attachmentCount: 0,
    createdAt: '',
    updatedAt: '',
  }
  handleOpenModal(newClaim, true)
}

const handleAttachmentAdd = async (files: FileList | null) => {
  if (!editingClaim || !files || editingClaim.id === 0) return

  const MAX_FILE_SIZE = 10 * 1024 * 1024
  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File "${file.name}" exceeds 10MB limit`)
      continue
    }
    try {
      const updatedClaim = await uploadCrewDeductionAttachment(editingClaim.id, file)
      setEditingClaim(updatedClaim)
      if (attachmentsModalClaim?.id === updatedClaim.id) {
        setAttachmentsModalClaim(updatedClaim)
      }
      await fetchClaims()
    } catch (error) {
      console.error('Error uploading attachment:', error)
      showErrorToast(error, `Failed to upload ${file.name}`, 'save')
    }
  }
}

const handleAttachmentRemove = async (attachmentId: number) => {
  if (!editingClaim || editingClaim.id === 0) return

  try {
    const updatedClaim = await deleteCrewDeductionAttachment(editingClaim.id, attachmentId)
    setEditingClaim(updatedClaim)
    if (attachmentsModalClaim?.id === updatedClaim.id) {
      setAttachmentsModalClaim(updatedClaim)
    }
    await fetchClaims()
  } catch (error) {
    console.error('Error removing attachment:', error)
    showErrorToast(error, 'Failed to remove attachment', 'delete')
  }
}

// View modal handlers
const handleOpenViewModal = (claim: CrewDeduction) => {
  setViewClaim(claim)
  setIsViewModalOpen(true)
}

const handleCloseViewModal = () => {
  setIsViewModalOpen(false)
  setViewClaim(null)
}

// Download individual deduction PDF
const handleDownloadDeductionPDF = (claim: CrewDeduction) => {
  try {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
    const pageWidth = doc.internal.pageSize.getWidth()
    const marginX = 40

    const formatCurrency = (value: number) =>
      value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

    const toNumber = (value: unknown): number => {
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : 0
    }

    const formatMonthLabel = (monthKey: string): string => {
      if (!monthKey) return '-'
      const [yearStr, monthStr] = monthKey.split('-')
      const year = Number(yearStr)
      const month = Number(monthStr)
      if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
        return monthKey
      }
      return new Date(year, month - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
    }

    doc.setFontSize(18)
    doc.text('Crew Deduction Details', marginX, 42)
    doc.setFontSize(10)
    doc.setTextColor(90, 90, 90)

    const filterLabels: string[] = []
    if (claim.claimMonth) filterLabels.push(`Month: ${formatMonthLabel(claim.claimMonth.slice(0, 7))}`)
    if (claim.crewName) filterLabels.push(`Crew: ${claim.crewName}`)
    if (claim.vesselName) filterLabels.push(`Vessel: ${claim.vesselName}`)
    if (filterLabels.length > 0) {
      doc.text(filterLabels.join(' | '), marginX, 60)
    }

    const headers = ['Field', 'Value']
    const body = [
      ['Crew Name', claim.crewName || '-'],
      ['Rank', claim.rankName || '-'],
      ['Vessel', claim.vesselName || '-'],
      ['Month', claim.claimMonth ? formatMonthLabel(claim.claimMonth.slice(0, 7)) : '-'],
      ['Status', claim.status || '-'],
      ['Cash Advance from Office', formatCurrency(toNumber(claim.deductionCashAdvanceFromOffice))],
      ['Bond', formatCurrency(toNumber(claim.deductionBond))],
      ['Internet Card', formatCurrency(toNumber(claim.deductionInternetCard))],
      ['Other Deductions', formatCurrency(toNumber(claim.deductionOtherDeductions))],
      ['First Time Deduction', formatCurrency(toNumber(claim.firstTimeDeduction))],
      ['Total Deductions', formatCurrency(toNumber(claim.deductionsTotal))],
      ['Remarks', claim.remarks || '-'],
      ['Office Remarks', claim.officeRemarks || '-'],
    ]

    ;(doc as any).autoTable({
      startY: 80,
      head: [headers],
      body,
      styles: { fontSize: 10, cellPadding: 5 },
      headStyles: { fillColor: [28, 72, 126], textColor: [255, 255, 255], fontStyle: 'bold' },
      bodyStyles: { textColor: [30, 30, 30] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: marginX, right: marginX },
      theme: 'grid',
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 150 },
        1: { cellWidth: 'auto' },
      },
    })

    doc.save(`crew_deduction_${claim.crewName}_${claim.claimMonth || 'all'}_${new Date().toISOString().split('T')[0]}.pdf`)
    toast.success('PDF downloaded successfully')
  } catch (error) {
    console.error('Error generating PDF:', error)
    showErrorToast(error, 'Failed to generate PDF', 'load')
  }
}

// Attachments modal handlers
const handleOpenAttachmentsModal = (claim: CrewDeduction) => {
  setAttachmentsModalClaim(claim)
  setIsAttachmentsModalOpen(true)
}

const handleCloseAttachmentsModal = () => {
  setIsAttachmentsModalOpen(false)
  setAttachmentsModalClaim(null)
}

// Review modal handlers
const handleOpenReviewModal = (claim: CrewDeduction) => {
  if (canEditClaim(claim) && isRowDirty(claim)) {
    toast.info('Save your deduction changes before office review')
    return
  }
  setReviewingClaim(claim)
  setIsReviewModalOpen(true)
}

const handleCloseReviewModal = () => {
  setIsReviewModalOpen(false)
  setReviewingClaim(null)
}

const handleSaveReviewOnly = async (officeRemarks: string) => {
  if (!reviewingClaim) return
  setIsReviewSaving(true)
  try {
    await updateCrewDeduction(reviewingClaim.id, {
      officeRemarks: officeRemarks.trim(),
    })
    toast.success('Office review remarks saved')
    await fetchClaims()
    handleCloseReviewModal()
  } catch (error) {
    console.error('Error saving office review:', error)
    showErrorToast(error, 'Failed to save office review', 'save')
  } finally {
    setIsReviewSaving(false)
  }
}

const handleCloseSatisfactory = async (officeRemarks: string) => {
  if (!reviewingClaim) return
  setIsReviewSaving(true)
  try {
    await updateCrewDeduction(reviewingClaim.id, {
      officeRemarks: officeRemarks.trim(),
    })
    await reviewCrewDeduction(reviewingClaim.id)
    toast.success('Deduction closed satisfactorily')
    await fetchClaims()
    handleCloseReviewModal()
  } catch (error) {
    console.error('Error closing deduction:', error)
    showErrorToast(error, 'Failed to close deduction', 'save')
  } finally {
    setIsReviewSaving(false)
  }
}

const startIndex = sortedClaims.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1
const endIndex = Math.min(currentPage * rowsPerPage, sortedClaims.length)

// Render status badge
const renderStatusBadge = (status: DeductionStatus) => {
  switch (status) {
    case 'DRAFT':
      return <span className='badge badge-light-secondary fw-bold'>Draft</span>
    case 'SUBMITTED':
      return <span className='badge badge-light-warning fw-bold'>Submitted</span>
    case 'REVIEWED':
      return <span className='badge badge-light-success fw-bold'>Reviewed</span>
    case 'REJECTED':
      return <span className='badge badge-light-danger fw-bold'>Rejected</span>
    default:
      return <span className='badge badge-light fw-bold'>{status}</span>
  }
}

const canEditClaim = (claim: CrewDeduction) =>
  (isSuperAdmin || hasPermission('UPDATE_CREW_DEDUCTION')) &&
  ((claim.status === 'DRAFT' && (isSuperAdmin || !isOfficeRole ||
    (canManageOfficeDraft && claim.canManageFinancialDraft === true))) || (claim.status === 'SUBMITTED' && canEditCrewDataAtOfficeReview(
    {isSuperAdmin, isCompanyAdmin: baseIsCompanyAdmin, isCompanyGroupAdmin, isOperator},
    hasPermission('UPDATE_CREW_DEDUCTION'), canApprove
  )))

const splitClaimRemarks = (claim: CrewDeduction) => {
  const normal = (claim.remarks || '').trim()
  const office = (claim.officeRemarks || '').trim()
  if (office) {
    return { normalRemarks: normal, officeRemarks: office }
  }

  // Backward compatibility for older records where office text was appended in remarks.
  const source = normal
  if (!source) return { normalRemarks: '', officeRemarks: '' }

  const officePattern =
    /\[Office Review[^\]]*\]\s*([\s\S]*?)(?=(?:\n\n\[Office Review[^\]]*\])|$)/g
  const officeChunks: string[] = []
  let match: RegExpExecArray | null = null

  while ((match = officePattern.exec(source)) !== null) {
    const text = (match[1] || '').trim()
    if (text) officeChunks.push(text)
  }

  const normalRemarks = source
    .replace(/\n?\n?\[Office Review[^\]]*\]\s*[\s\S]*?(?=(?:\n\n\[Office Review[^\]]*\])|$)/g, '')
    .trim()

  return {
    normalRemarks,
    officeRemarks: officeChunks.join('\n\n'),
  }
}

// Render action buttons
const renderActions = (claim: CrewDeduction) => {
  const canEditRow = canEditClaim(claim)
  const rowDirty = isRowDirty(claim)
  const isNewRow = claim.id <= 0
  const rowSaving = Boolean(inlineSavingClaimIds[getDraftKey(claim)])

  return (
    <div className='d-flex justify-content-left gap-1'>
      {/* View - Everyone can view */}
      <button
        className='btn btn-icon btn-sm'
        title='View'
        onClick={() => handleOpenViewModal(claim)}
      >
        <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-3 text-primary' />
      </button>

      {/* Download - Download individual deduction PDF */}
      {canDownloadDeduction && (
        <button
          className='btn btn-icon btn-sm'
          title='Download PDF'
          onClick={() => handleDownloadDeductionPDF(claim)}
        >
          <KTSVG path='/media/icons/duotune/files/fil008.svg' className='svg-icon-3 text-success' />
        </button>
      )}

      {canEditRow && (
        <button
          type='button'
          className='btn btn-light-primary btn-sm'
          onClick={() => handleSaveInlineRow(claim)}
          disabled={rowSaving || (!rowDirty && !isNewRow)}
          title={rowDirty || isNewRow ? 'Save row' : 'No changes'}
        >
          {rowSaving ? 'Saving...' : 'Save'}
        </button>
      )}

      {/* Submit - Only creators for DRAFT claims */}
      {canSubmit && (!isOfficeRole || isSuperAdmin || claim.canManageFinancialDraft === true) && claim.status === 'DRAFT' && (
        <button
          type='button'
          className='btn btn-light-primary btn-sm'
          onClick={() => handleSubmitInlineRow(claim)}
          disabled={rowSaving}
        >
          Submit
        </button>
      )}

      {/* Review - Only reviewers/approvers for SUBMITTED claims */}
      {canApprove && claim.status === 'SUBMITTED' && (
        <button
          type='button'
          className='btn btn-light-warning btn-sm'
          onClick={() => handleOpenReviewModal(claim)}
        >
          Review
        </button>
      )}
    </div>
  )
}

// Render pagination
const renderPagination = () => {
  const pages = []
  const showPages = 5
  let startPage = Math.max(1, currentPage - 2)
  let endPage = Math.min(totalPages, startPage + showPages - 1)

  if (endPage - startPage < showPages - 1) {
    startPage = Math.max(1, endPage - showPages + 1)
  }

  if (startPage > 1) {
    pages.push(
      <li key={1} className='page-item'>
        <button
          className='page-link text-muted'
          style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            padding: '8px 12px',
            fontSize: '14px',
            minWidth: '40px',
            borderRadius: '6px',
          }}
          onClick={() => handlePageChange(1)}
        >
          1
        </button>
      </li>
    )

    if (startPage > 2) {
      pages.push(
        <li key='ellipsis1' className='page-item disabled'>
          <span
            className='page-link border-0 text-muted'
            style={{ backgroundColor: 'transparent', padding: '4px 8px' }}
          >
            ...
          </span>
        </li>
      )
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(
      <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
        <button
          className='page-link text-muted'
          style={{
            backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
            border: '1px solid #dee2e6',
            padding: '8px 12px',
            fontSize: '14px',
            minWidth: '40px',
            borderRadius: '6px',
            outline: 'none',
            boxShadow: 'none',
          }}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      </li>
    )
  }

  if (endPage < totalPages) {
    if (endPage < totalPages - 1) {
      pages.push(
        <li key='ellipsis2' className='page-item disabled'>
          <span
            className='page-link border-0 text-muted'
            style={{ backgroundColor: 'transparent', padding: '4px 8px' }}
          >
            ...
          </span>
        </li>
      )
    }

    pages.push(
      <li key={totalPages} className='page-item'>
        <button
          className='page-link text-muted'
          style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            padding: '8px 12px',
            fontSize: '14px',
            minWidth: '40px',
            borderRadius: '6px',
          }}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      </li>
    )
  }

  return pages
}

return (
  <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
    <div className='d-flex flex-column flex-column-fluid'>
      <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
        <div className='card'>
          <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
            <div>
              <h3 className='card-label text-dark fw-bold'>Crew Deduction</h3>
              <p className='text-muted mb-0 mt-1'>Submit and view crew deduction records.</p>
            </div>
            <div className='card-toolbar'>
              <div className='d-flex gap-2'>
                {canExportDeduction && (
                  <button
                    type='button'
                    className='btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                    style={{
                      backgroundColor: 'rgb(220, 38, 38)',
                      borderColor: 'rgb(220, 38, 38)',
                      borderRadius: '9.75px',
                      padding: '8px 13px',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px',
                      transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                      gap: '6.5px',
                    }}
                    onClick={() => setIsExportModalOpen(true)}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='14'
                      height='14'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      style={{ width: '13px', height: '13px' }}
                    >
                      <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'></path>
                      <polyline points='7 10 12 15 17 10'></polyline>
                      <line x1='12' y1='15' x2='12' y2='3'></line>
                    </svg>
                    Export PDF
                  </button>
                )}
                {canCreate && (
                  <button
                    type='button'
                    className='btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                    style={{
                      backgroundColor: 'rgb(29, 78, 216)',
                      borderColor: 'rgb(29, 78, 216)',
                      borderRadius: '9.75px',
                      padding: '8px 13px',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px',
                      transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                      gap: '6.5px',
                    }}
                    onClick={handleNewClaim}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='14'
                      height='14'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      style={{ width: '13px', height: '13px' }}
                    >
                      <line x1='12' y1='5' x2='12' y2='19'></line>
                      <line x1='5' y1='12' x2='19' y2='12'></line>
                    </svg>
                    Add Deduction
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className='card-body py-4 bg-white border-top'>
            <div className='row gx-3 gy-3'>
              <div className='col-md-3'>
                <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>
                  {useCrewDropdownFilter ? 'Crew' : 'Search'}
                </label>
                {useCrewDropdownFilter ? (
                  <select
                    className='form-select'
                    value={selectedCrewId}
                    onChange={(event) =>
                      setSelectedCrewId(event.target.value ? Number(event.target.value) : '')
                    }
                  >
                    <option value=''>All Crew</option>
                    {crewFilterOptions.map((crew) => (
                      <option key={crew.id} value={crew.id}>
                        {crew.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type='text'
                    className='form-control'
                    placeholder='Search crew or rank'
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                )}
              </div>

              <div className='col-md-2'>
                <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>
                  Month
                </label>
                <input
                  type='month'
                  className='form-control'
                  value={selectedMonth}
                  onChange={(event) => handleSelectedMonthChange(event.target.value)}
                />
              </div>

              <div className='col-md-2'>
                <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>
                  Status
                </label>
                <select
                  className='form-select'
                  value={statusFilter}
                  onChange={(event) =>
                    setStatusFilter(event.target.value as 'All' | DeductionStatus)
                  }
                >
                  <option value='All'>All Status</option>
                  {canViewDrafts && <option value='DRAFT'>Draft</option>}
                  <option value='SUBMITTED'>Submitted</option>
                </select>
              </div>
            </div>

            {showVesselFilter && (
              <div className='row gx-3 gy-3 mt-2'>
                {showVesselFilter && (
                  <div className='col-md-3'>
                    <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>
                      Crew Status
                    </label>
                    <select
                      className='form-select'
                      value={crewStatusFilter}
                      onChange={(event) =>
                        setCrewStatusFilter(event.target.value as 'ALL' | 'SIGNED_ON' | 'SIGNED_OFF')
                      }
                    >
                      <option value='ALL'>All</option>
                      <option value='SIGNED_ON'>Signed On</option>
                      <option value='SIGNED_OFF'>Signed Off</option>
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Summary Cards */}
          <div className='card-body py-4 bg-white border-top'>
            <div className='row g-3'>
              <div className='col-md-3'>
                <div className='border rounded p-3'>
                  <div className='text-muted fs-7 fw-semibold'>Total Records</div>
                  <div className='fs-3 fw-bold text-dark'>{totals.totalClaims}</div>
                </div>
              </div>
              <div className='col-md-3'>
                <div className='border rounded p-3'>
                  <div className='text-muted fs-7 fw-semibold'>Submitted</div>
                  <div className='fs-3 fw-bold text-warning'>{totals.submitted}</div>
                </div>
              </div>
              <div className='col-md-3'>
                <div className='border rounded p-3'>
                  <div className='text-muted fs-7 fw-semibold'>Total Deduction</div>
                  <div className='fs-3 fw-bold text-primary'>
                    {formatCurrency(totals.totalAmount)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className='card-body py-4 bg-white border-top'>
            <div className='report-table table-responsive'>
              <div style={{ overflowX: 'auto' }} onWheel={handleTableHorizontalWheelScroll}>
                <table className='table table-bordered align-middle'>
                    <thead className='table-header text-start'>
                      <tr>
                        <th className='text-center text-nowrap' style={{ minWidth: '70px' }}>
                          Sr/No
                        </th>
                        <th style={{ minWidth: '180px' }}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Name</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('crewName')}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${sortConfig.key === 'crewName'
                                    ? sortConfig.direction === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                  }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{ minWidth: '150px' }}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Rank</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('rankName')}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${sortConfig.key === 'rankName'
                                    ? sortConfig.direction === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                  }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{ minWidth: '150px' }} className='text-nowrap'>
                          Deduction Month
                        </th>
                        <th style={{ minWidth: '170px' }} className='text-nowrap'>
                          Cash Advance
                        </th>
                        <th style={{ minWidth: '120px' }} className='text-nowrap'>
                          Bond
                        </th>
                        <th style={{ minWidth: '170px' }} className='text-nowrap'>
                          Deduction - Internet Card
                        </th>
                        <th style={{ minWidth: '140px' }} className='text-nowrap'>
                          Other Deductions
                        </th>
                        <th style={{ minWidth: '140px' }}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Total Deduction</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('total')}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${sortConfig.key === 'total'
                                    ? sortConfig.direction === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                  }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{ minWidth: '120px' }} className='text-nowrap'>
                          Attachments
                        </th>
                        <th style={{ minWidth: '160px', maxWidth: '220px' }} className='text-nowrap'>
                          Remarks
                        </th>
                        <th style={{ minWidth: '160px', maxWidth: '220px' }} className='text-nowrap'>
                          Office Remarks
                        </th>
                        <th style={{ minWidth: '120px' }}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Status</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('status')}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${sortConfig.key === 'status'
                                    ? sortConfig.direction === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                  }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th
                          style={{ minWidth: '170px', textAlign: 'center' }}
                          className='text-nowrap'
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className='table-body text-start'>
                      {(isLoading || isCrewLoading) ? (
                        <TableSkeleton rows={rowsPerPage || 10} cols={14} />
                      ) : sortedClaims.length === 0 ? (
                        <tr>
                          <td colSpan={14} className='text-center text-muted py-5'>
                            No deduction records found for the selected filters.
                          </td>
                        </tr>
                      ) : (
                        currentRecords.map((claim, idx) => {
                          const { normalRemarks, officeRemarks } = splitClaimRemarks(claim)
                          const canEditRow = canEditClaim(claim)
                          const draft = getInlineDraft(claim)
                          const currencySymbol = getCurrencySymbol(getClaimCurrency(claim))
                          const draftTotal = calculateDraftTotal(draft)
                          const rowKey =
                            claim.id > 0
                              ? `claim-${claim.id}`
                              : `crew-${claim.crewId}-${getMonthKey(claim.claimMonth) || 'na'}`
                          return (
                            <tr key={rowKey}>
                              <td className='text-center'>{indexOfFirstRecord + idx + 1}</td>
                              <td>{claim.crewName}</td>
                              <td>{claim.rankName || '—'}</td>
                              <td>
                                {formatMonthKeyLabel(
                                  lockedMonthKey || getMonthKey(claim.claimMonth)
                                )}
                              </td>
                              <td>
                                {canEditRow ? (
                                  <div className='input-group input-group-sm'>
                                    <span className='input-group-text'>{currencySymbol}</span>
                                    <input
                                      type='number'
                                      className='form-control form-control-sm'
                                      placeholder='0.00'
                                      value={draft.deductionCashAdvanceFromOffice}
                                      onWheel={blurNumberInputOnWheel}
                                      onKeyDown={preventNumberInputArrowStep}
                                      onChange={(event) =>
                                        updateInlineDraft(
                                          claim,
                                          'deductionCashAdvanceFromOffice',
                                          event.target.value
                                        )
                                      }
                                    />
                                  </div>
                                ) : (
                                  formatClaimAmount(
                                    claim,
                                    claim.deductionCashAdvanceFromOffice || 0
                                  )
                                )}
                              </td>
                              <td>
                                {canEditRow ? (
                                  <div className='input-group input-group-sm'>
                                    <span className='input-group-text'>{currencySymbol}</span>
                                    <input
                                      type='number'
                                      className='form-control form-control-sm'
                                      placeholder='0.00'
                                      value={draft.deductionBond}
                                      onWheel={blurNumberInputOnWheel}
                                      onKeyDown={preventNumberInputArrowStep}
                                      onChange={(event) =>
                                        updateInlineDraft(
                                          claim,
                                          'deductionBond',
                                          event.target.value
                                        )
                                      }
                                    />
                                  </div>
                                ) : (
                                  formatClaimAmount(claim, claim.deductionBond || 0)
                                )}
                              </td>
                              <td>
                                {canEditRow ? (
                                  <div className='input-group input-group-sm'>
                                    <span className='input-group-text'>{currencySymbol}</span>
                                    <input
                                      type='number'
                                      className='form-control form-control-sm'
                                      placeholder='0.00'
                                      value={draft.deductionInternetCard}
                                      onWheel={blurNumberInputOnWheel}
                                      onKeyDown={preventNumberInputArrowStep}
                                      onChange={(event) =>
                                        updateInlineDraft(
                                          claim,
                                          'deductionInternetCard',
                                          event.target.value
                                        )
                                      }
                                    />
                                  </div>
                                ) : (
                                  formatClaimAmount(claim, claim.deductionInternetCard || 0)
                                )}
                              </td>
                              <td>
                                {canEditRow ? (
                                  <div className='input-group input-group-sm'>
                                    <span className='input-group-text'>{currencySymbol}</span>
                                    <input
                                      type='number'
                                      className='form-control form-control-sm'
                                      placeholder='0.00'
                                      value={draft.deductionOtherDeductions}
                                      onWheel={blurNumberInputOnWheel}
                                      onKeyDown={preventNumberInputArrowStep}
                                      onChange={(event) =>
                                        updateInlineDraft(
                                          claim,
                                          'deductionOtherDeductions',
                                          event.target.value
                                        )
                                      }
                                    />
                                  </div>
                                ) : (
                                  formatClaimAmount(claim, claim.deductionOtherDeductions || 0)
                                )}
                              </td>
                              <td className='fw-bold'>
                                {canEditRow
                                  ? `${currencySymbol}${formatCurrency(draftTotal)}`
                                  : formatClaimAmount(claim, calculateRowTotal(claim))}
                              </td>
                              <td>
                                {(claim.attachmentCount || 0) > 0 ? (
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-primary text-nowrap'
                                    onClick={() => handleOpenAttachmentsModal(claim)}
                                  >
                                    View Files ({claim.attachmentCount || 0})
                                  </button>
                                ) : (
                                  <span className='text-muted'>No attachments</span>
                                )}
                              </td>
                              <td>
                                <div className='d-flex align-items-start gap-1'>
                                  <RemarksCell
                                    text={canEditRow ? (draft.remarks ?? normalRemarks) : normalRemarks}
                                    title='Crew Deduction Remarks'
                                    subtitle={claim.crewName || ''}
                                    onOpenModal={(title, subtitle, text) => {
                                      setRemarksModal({ visible: true, title, subtitle, text })
                                    }}
                                  />
                                  {canEditRow && (
                                    <button
                                      type='button'
                                      className='btn btn-icon btn-xs btn-light-primary ms-1'
                                      style={{ width: '22px', height: '22px', minWidth: '22px' }}
                                      title='Edit remarks'
                                      onClick={() => {
                                        setEditingRemarksRecord({
                                          claim,
                                          text: draft.remarks ?? normalRemarks ?? '',
                                        })
                                      }}
                                    >
                                      <KTSVG path='/media/map/edit-active.svg' className='svg-icon-4' />
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td>
                                <RemarksCell
                                  text={officeRemarks}
                                  title='Office Remarks'
                                  subtitle={claim.crewName || ''}
                                  onOpenModal={(title, subtitle, text) => {
                                    setRemarksModal({ visible: true, title, subtitle, text })
                                  }}
                                />
                              </td>
                              <td className='text-nowrap'>{renderStatusBadge(claim.status)}</td>
                              <td className='text-nowrap'>{renderActions(claim)}</td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={13} className='text-end fw-bold text-dark'>
                          Total Deduction
                        </td>
                        <td className='fw-bold text-dark text-center'>
                          {formatCurrency(totals.totalAmount)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
              </div>

              {/* Pagination */}
              <div
                className='pagination-wrapper d-flex justify-content-between align-items-center py-3 border-top'
                style={{
                  position: 'static',
                  bottom: 0,
                  backgroundColor: '#fff',
                  zIndex: 10,
                }}
              >
                <div className='d-flex align-items-center'>
                  <span className='text-muted me-2'>Rows per page</span>
                  <select
                    className='form-select'
                    style={{
                      borderRadius: '20px',
                      width: '70px',
                      border: '1px solid #dee2e6',
                      fontSize: '14px',
                      padding: '4px 8px',
                    }}
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                <div className='d-flex align-items-center'>
                  <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                    Showing{' '}
                    <strong>
                      {startIndex}-{endIndex}
                    </strong>{' '}
                    of <strong>{sortedClaims.length}</strong>
                  </span>

                  <nav>
                    <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className='page-link text-muted'
                          style={{
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            padding: '8px 12px',
                            fontSize: '14px',
                            borderRadius: '6px',
                          }}
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          ‹
                        </button>
                      </li>
                      {renderPagination()}
                      <li
                        className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''
                          }`}
                      >
                        <button
                          className='page-link text-muted'
                          style={{
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            padding: '8px 12px',
                            fontSize: '14px',
                            borderRadius: '6px',
                          }}
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages || totalPages === 0}
                        >
                          ›
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Crew Claim Modal */}
    <CrewDeductionModal
      isOpen={isModalOpen}
      isSuperAdmin={isSuperAdmin}
      isManagementEntry={canManageOfficeDraft}
      isOfficeEntry={isOfficeRole && !isSuperAdmin}
      showCompanyFilter={showCompanyFilter}
      showSubCompanyFilter={showSubCompanyFilter && !fixedCompanyAdminId}
      canCreate={canCreate}
      canEditDraft={!!editingClaim && canEditClaim(editingClaim)}
      canReview={canApprove}
      isNewClaim={isNewClaim}
      restrictToMedicalTravelOnFirstFill={isCrew && !isCrewMaster}
      lockCrewSelection={isCrew && !isCrewMaster}
      claim={editingClaim}
      ranks={ranks}
      crewMembers={selectableModalCrewMembers}
      companies={companies}
      companyAdmins={companyAdmins}
      vessels={vessels}
      selectedCompanyGroupId={fixedCompanyGroupId ?? modalCompanyGroupId}
      selectedCompanyAdminId={fixedCompanyAdminId ?? modalCompanyAdminId}
      selectedVesselId={modalVesselId}
      onCompanyGroupChange={(next) => {
        setModalCompanyGroupId(next)
        setModalCompanyAdminId('')
        setModalVesselId('')
      }}
      onCompanyAdminChange={(next) => {
        setModalCompanyAdminId(next)
        setModalVesselId('')
      }}
      onVesselChange={(next) => setModalVesselId(next)}
      onClose={handleCloseModal}
      onSave={handleSaveClaim}
      onChange={(nextClaim) => setEditingClaim(nextClaim)}
      onAttachmentAdd={handleAttachmentAdd}
      onAttachmentRemove={handleAttachmentRemove}
      hasUsedFirstTimeDeductionForCrew={hasUsedFirstTimeDeduction}
      calculateRowTotal={calculateRowTotal}
      formatCurrency={formatCurrency}
      isLoading={isSaving}
      crewSignOnDateById={crewSignOnDateByCrewId}
    />

    {/* View Claim Modal */}
    <ViewCrewDeductionModal
      visible={isViewModalOpen}
      claim={viewClaim}
      onClose={handleCloseViewModal}
      onViewAttachments={() => {
        if (viewClaim) {
          handleCloseViewModal()
          handleOpenAttachmentsModal(viewClaim)
        }
      }}
    />

    {/* Review Claim Modal */}
    <CrewDeductionReviewModal
      visible={isReviewModalOpen}
      claim={reviewingClaim}
      onClose={handleCloseReviewModal}
      onSave={handleSaveReviewOnly}
      onCloseSatisfactory={handleCloseSatisfactory}
      isLoading={isReviewSaving}
    />

    {/* Attachments Modal */}
    <CrewDeductionAttachmentsModal
      visible={isAttachmentsModalOpen}
      title={`Attachments - ${attachmentsModalClaim?.crewName || 'Claim'}`}
      subtitle={attachmentsModalClaim?.rankName || null}
      loading={false}
      items={(attachmentsModalClaim?.attachments || []).map((att) => ({
        id: att.id,
        fileName: att.fileName,
        viewUrl: crewDeductionAttachmentViewUrl(attachmentsModalClaim!.id, att.id),
        downloadUrl: crewDeductionAttachmentDownloadUrl(attachmentsModalClaim!.id, att.id),
        fileType: att.fileType,
        fileSize: att.fileSize,
      }))}
      onClose={handleCloseAttachmentsModal}
      onViewFile={(item) => {
        setFileModal({
          visible: true,
          title: item.fileName,
          fileName: item.fileName,
          viewUrl: item.viewUrl,
          downloadUrl: item.downloadUrl,
        })
      }}
    />

    {/* File Viewer Modal */}
    <FileViewerModal
      visible={fileModal.visible}
      title={fileModal.title}
      fileName={fileModal.fileName}
      viewUrl={fileModal.viewUrl}
      downloadUrl={fileModal.downloadUrl}
      onClose={() => setFileModal((prev) => ({ ...prev, visible: false }))}
    />

    {/* Remarks Modal */}
    <RemarksModal
      visible={remarksModal.visible}
      title={remarksModal.title}
      subtitle={remarksModal.subtitle}
      remarksText={remarksModal.text}
      onClose={() => setRemarksModal(prev => ({ ...prev, visible: false }))}
    />

    {/* Inline Remarks Edit Modal */}
    {editingRemarksRecord && (
      <div
        style={{
          background: 'rgba(0,0,0,0.5)',
          position: 'fixed',
          inset: 0,
          zIndex: 1060,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        tabIndex={-1}
        onClick={() => setEditingRemarksRecord(null)}
      >
        <div
          className='bg-white text-dark rounded shadow-lg d-flex flex-column'
          style={{
            width: '90vw',
            maxWidth: '600px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className='d-flex align-items-center justify-content-between border-bottom px-5 py-4'>
            <h5 className='modal-title text-dark m-0 fw-bold'>
              Edit Remarks {editingRemarksRecord.claim.crewName ? `- ${editingRemarksRecord.claim.crewName}` : ''}
            </h5>
            <button
              type='button'
              className='btn-close'
              onClick={() => setEditingRemarksRecord(null)}
            />
          </div>
          <div className='p-5'>
            <label className='form-label fw-semibold fs-7 text-muted mb-2'>
              Remarks
            </label>
            <textarea
              className='form-control'
              rows={5}
              value={editingRemarksRecord.text}
              onChange={(e) =>
                setEditingRemarksRecord((prev) =>
                  prev ? { ...prev, text: e.target.value } : null
                )
              }
              placeholder='Enter deduction remarks...'
              style={{ fontSize: '13.5px', lineHeight: '1.6' }}
            />
          </div>
          <div className='border-top d-flex justify-content-end gap-2 px-5 py-3 bg-light'>
            <button
              type='button'
              className='btn btn-light btn-sm'
              onClick={() => setEditingRemarksRecord(null)}
            >
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-primary btn-sm'
              onClick={() => {
                updateInlineDraft(
                  editingRemarksRecord.claim,
                  'remarks',
                  editingRemarksRecord.text
                )
                setEditingRemarksRecord(null)
              }}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Export Modal */}
    <CrewDeductionExportModal
      open={isExportModalOpen}
      onClose={() => setIsExportModalOpen(false)}
      initialFilters={{
        companyGroupId: selectedCompanyGroupId ? Number(selectedCompanyGroupId) : null,
        companyAdminId: selectedCompanyAdminId ? Number(selectedCompanyAdminId) : null,
        vesselId: selectedVesselId ? Number(selectedVesselId) : null,
        crewId: selectedCrewId ? Number(selectedCrewId) : null,
        month: selectedMonth || null,
      }}
      currentUser={currentUser}
      isSuperAdmin={isSuperAdmin}
      isCompanyAdmin={isCompanyAdmin}
      isCompanyGroupAdmin={isCompanyGroupAdmin}
      isOperator={isOperator}
      isCrewMaster={isCrewMaster}
      isCrew={isCrew}
      vessels={vessels}
    />

    <ToastContainer
      position='top-center'
      autoClose={3000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      style={{ top: '5rem' }}
    />
  </div>
)
}

export default CrewDeductions
